import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
	const [searchParams] = useSearchParams();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [validatingToken, setValidatingToken] = useState(true);
	const [tokenValid, setTokenValid] = useState(false);
	const [resetSuccess, setResetSuccess] = useState(false);
	const navigate = useNavigate();
	const token = searchParams.get('token');

	useEffect(() => {
		if (!token) {
			toast.error('Invalid reset link');
			navigate('/auth');
			return;
		}
		validateToken();
	}, [token]);

	const validateToken = async () => {
		try {
			await axios.post(`${API}/auth/validate-reset-token`, { token });
			setTokenValid(true);
		} catch (err) {
			toast.error('Invalid or expired reset link');
			setTokenValid(false);
		}
		setValidatingToken(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (password.length < 6) {
			toast.error('Password must be at least 6 characters');
			return;
		}
		
		if (password !== confirmPassword) {
			toast.error('Passwords do not match');
			return;
		}

		setLoading(true);
		try {
			await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
			setResetSuccess(true);
			toast.success('Password reset successfully!');
			setTimeout(() => navigate('/auth'), 3000);
		} catch (err) {
			let errorMessage = 'Failed to reset password';
			if (err.response?.data?.detail) {
				const detail = err.response.data.detail;
				if (Array.isArray(detail)) {
					errorMessage = detail.map(e => e.msg || e.message).join(', ');
				} else if (typeof detail === 'string') {
					errorMessage = detail;
				}
			}
			toast.error(errorMessage);
		}
		setLoading(false);
	};

	// Reusable hero block — matches the login page
	const Hero = () => (
		<div className="auth-hero">
			<span className="auth-hero__star auth-hero__star--1" />
			<span className="auth-hero__star auth-hero__star--2" />
			<span className="auth-hero__star auth-hero__star--3" />
			<span className="auth-hero__star auth-hero__star--4" />
			<span className="auth-hero__star auth-hero__star--5" />
			<div className="auth-hero__brand">
				<img className="auth-hero__logo" src="/images/orbit-logo.svg" alt="Orbit" />
				<span className="auth-hero__name">Orbit Analytics</span>
			</div>
			<span className="auth-hero__cloud auth-hero__cloud--left" />
			<span className="auth-hero__cloud auth-hero__cloud--right" />
			<span className="auth-hero__cloud auth-hero__cloud--center" />
		</div>
	);

	if (validatingToken) {
		return (
			<div className="auth-page-wrapper">
				<div className="auth-container-new">
					<div className="auth-card-new">
						<Hero />
						<div className="loading-container">
							<div className="loading-spinner"></div>
							<p>Validating reset link...</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!tokenValid) {
		return (
			<div className="auth-page-wrapper">
				<div className="auth-container-new">
					<div className="auth-card-new">
						<Hero />
						<div className="error-message">
							<AlertCircle className="w-16 h-16 text-red-500" />
							<h2>Invalid Reset Link</h2>
							<p>This password reset link is invalid or has expired.</p>
							<Button onClick={() => navigate('/forgot-password')} className="mt-4">
								Request New Link
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (resetSuccess) {
		return (
			<div className="auth-page-wrapper">
				<div className="auth-container-new">
					<div className="auth-card-new">
						<Hero />
						<div className="success-message">
							<CheckCircle className="w-16 h-16 text-green-500" />
							<h2>Password Reset Successful!</h2>
							<p>You can now sign in with your new password.</p>
							<p className="text-sm text-gray-500 mt-4">Redirecting to login...</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="auth-page-wrapper">
			<div className="auth-container-new">
				<div className="auth-card-new">
					<Hero />

					<div className="auth-header-new">
						<h2>Reset Password</h2>
						<p>Enter your new password below</p>
					</div>

					<form onSubmit={handleSubmit} className="auth-form-new">
						<div className="form-group">
							<label>New Password</label>
							<Input
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								className="auth-input"
								minLength={6}
							/>
							<small className="text-gray-500">At least 6 characters</small>
						</div>
						<div className="form-group">
							<label>Confirm Password</label>
							<Input
								type="password"
								placeholder="••••••••"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
								className="auth-input"
							/>
						</div>
						<Button type="submit" disabled={loading} className="auth-submit-btn">
							{loading ? 'Resetting...' : 'Reset Password'}
						</Button>
					</form>

					<div className="auth-footer">
						<p>
							Remember your password?{' '}
							<button onClick={() => navigate('/auth')} className="auth-switch-link">
								Sign In
							</button>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ResetPasswordPage;
